import { ApiService } from './ApiService';
import {
  FeedDailyTotal,
  FeedRegistrationAnimals,
  FeedRegistrationsPage,
  FeedRegistrationsQuery
} from '../models/feed-registration';

/**
 * Reads the manual feed registrations behind the farrowing-pen feeding flow. The dashboard is
 * read-only here - registering and editing feed happens in the mobile app.
 *
 * The wrapper earns its keep by dropping empty filters (an empty `LocationIds` is a different, much
 * larger request than an absent one) and by translating the UI's zero-based page to the API's
 * one-based `Page`.
 */
export class FeedRegistrationsService extends ApiService {
  getRegistrations = async (farmId: string, query: FeedRegistrationsQuery): Promise<FeedRegistrationsPage> => {
    const result = await this.api.feedRegistrations.feedRegistrationsGet({
      farmId,
      LocationIds: query.locationIds.length > 0 ? query.locationIds : undefined,
      StartDate: query.startDate || undefined,
      EndDate: query.endDate || undefined,
      Page: query.page + 1,
      Size: query.size
    });

    return result.data;
  };

  getDailyTotals = async (
    farmId: string,
    query: Pick<FeedRegistrationsQuery, 'locationIds' | 'startDate' | 'endDate'>
  ): Promise<FeedDailyTotal[]> => {
    // Built by hand rather than with BuildQueryString, which cannot repeat a key for a list.
    const params = new URLSearchParams();
    for (const locationId of query.locationIds) params.append('LocationIds', locationId);
    if (query.startDate) params.append('StartDate', query.startDate);
    if (query.endDate) params.append('EndDate', query.endDate);

    const result = await this.Fetch(`/farms/${farmId}/feed-registrations/daily-totals?${params}`, 'GET');

    return this.FromResult<FeedDailyTotal[]>(result);
  };

  getRegistrationAnimals = async (farmId: string, registrationId: string): Promise<FeedRegistrationAnimals> => {
    const result = await this.Fetch(`/farms/${farmId}/feed-registrations/${registrationId}/animals`, 'GET');

    return this.FromResult<FeedRegistrationAnimals>(result);
  };
}

export default FeedRegistrationsService;
